/**
 * ComDesk — the telephone channel (REQ-023).
 *
 * Two things on one page: the switch's own state, and the call log.
 *
 * The state is first, and not decoration. A call log that is empty because it
 * was a quiet morning and one that is empty because the switch cannot place a
 * call look identical, and only one of them is somebody's problem today. So
 * the page says which it is, including the honest "no phone line yet" that is
 * the truth until a carrier contract exists.
 *
 * There is no player and no transcript, because there is no recording. See
 * REQ-023 AC-005: recording needs the consent of every party before the call
 * starts, and a back office that cannot yet capture that consent must not
 * record.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Plus,
  Search,
} from "lucide-react";
import { useState } from "react";
import { useI18nStore } from "@/stores/i18nStore";
import { getLocaleString } from "@/utils/dateLocalization";
import apiClient from "@/services/api/apiClient";

type Direction = "inbound" | "outbound";
type Outcome =
  | "connected"
  | "no_answer"
  | "voicemail"
  | "wrong_number"
  | "call_back";

interface Call {
  id: string;
  direction: Direction;
  number: string;
  counterpartyName?: string;
  startedAt: string;
  durationSeconds: number;
  outcome: Outcome;
  notes?: string;
  handledBy: string;
  source: "manual" | "freeswitch";
}

interface MyExtension {
  extension: string;
  displayName: string;
  sipPassword: string;
  sipDomain: string;
  created: boolean;
}

interface SwitchState {
  provider: string;
  capturesAutomatically: boolean;
  configured: boolean;
  reachable: boolean;
  detail: string;
  hasRegisteredGateway: boolean;
}

const OUTCOME_LABEL: Record<Outcome, string> = {
  connected: "Connected",
  no_answer: "No answer",
  voicemail: "Voicemail",
  wrong_number: "Wrong number",
  call_back: "Call back",
};

const OUTCOME_STYLE: Record<Outcome, string> = {
  connected: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  no_answer: "bg-gray-50 text-gray-600 ring-gray-500/20",
  voicemail: "bg-sky-50 text-sky-700 ring-sky-600/20",
  wrong_number: "bg-amber-50 text-amber-700 ring-amber-600/20",
  call_back: "bg-violet-50 text-violet-700 ring-violet-600/20",
};

const duration = (seconds: number) => {
  if (!seconds) return "—";
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
};

const when = (iso: string) => {
  const language = useI18nStore.getState().language;
  return new Date(iso).toLocaleString(getLocaleString(language), {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: language === "en",
  });
};

/** What the switch is doing, said plainly. */
function SwitchBanner({ state }: { state: SwitchState }) {
  if (!state.configured || !state.reachable) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <p className="font-medium">
            The telephone switch is not reachable from this server.
          </p>
          <p className="mt-1 text-amber-800">
            Calls placed elsewhere will not appear here on their own. You can
            still log one by hand. {state.detail}
          </p>
        </div>
      </div>
    );
  }

  if (!state.hasRegisteredGateway) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <p className="font-medium">
            The switch is running, but there is no phone line yet.
          </p>
          <p className="mt-1 text-sky-800">
            No SIP trunk is registered, so no call can be placed or received
            through it. Calls logged below are the ones staff record by hand.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
      <CheckCircle2 className="h-4 w-4 shrink-0" />
      <p>
        The switch is running with a registered line. Finished calls are
        recorded automatically.
      </p>
    </div>
  );
}

/**
 * The extension belonging to whoever is looking.
 *
 * The password is hidden until asked for. It is the credential for a device
 * that can place calls at our expense, and a page left open on a desk should
 * not be showing it.
 */
function MyExtensionCard({ mine }: { mine: MyExtension }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Your extension
        </span>
        <span className="text-2xl font-semibold tabular-nums text-gray-900">
          {mine.extension}
        </span>
      </div>
      <p className="mt-2 text-sm text-gray-600">
        Colleagues reach you by dialling {mine.extension}. Dial{" "}
        <span className="font-medium">*98</span> for your voicemail.
      </p>
      <div className="mt-3 flex items-center gap-2 text-sm">
        <span className="text-gray-500">SIP password</span>
        <code className="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-900">
          {shown ? mine.sipPassword : "••••••••••••"}
        </code>
        <button
          onClick={() => setShown((open) => !open)}
          className="text-sky-700 hover:underline"
        >
          {shown ? "Hide" : "Show"}
        </button>
      </div>
      {!mine.sipDomain && (
        <p className="mt-3 text-sm text-amber-800">
          You cannot register a softphone yet: the switch does not accept
          connections from outside the server. The extension works between staff
          once it does.
        </p>
      )}
    </div>
  );
}

export default function PhonePage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [logging, setLogging] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const state = useQuery({
    queryKey: ["comdesk", "switch"],
    queryFn: async () => {
      const response = await apiClient.get<{ data: SwitchState }>(
        "/staff/comdesk/calls/status",
      );
      return response.data.data;
    },
    // Slow on purpose: this reaches across to the switch, and the answer
    // changes when somebody configures a trunk, not second by second.
    refetchInterval: 60_000,
  });

  const mine = useQuery({
    queryKey: ["comdesk", "my-extension"],
    queryFn: async () => {
      const response = await apiClient.get<{ data: MyExtension }>(
        "/staff/comdesk/calls/extension",
      );
      return response.data.data;
    },
  });

  const calls = useQuery({
    queryKey: ["comdesk", "calls", search],
    queryFn: async () => {
      const query =
        search.trim().length > 1
          ? `?q=${encodeURIComponent(search.trim())}`
          : "";
      const response = await apiClient.get<{ data: { calls: Call[] } }>(
        `/staff/comdesk/calls${query}`,
      );
      return response.data.data.calls;
    },
    refetchInterval: 30_000,
  });

  const logCall = useMutation({
    mutationFn: async (form: FormData) => {
      const outcome = String(form.get("outcome")) as Outcome;
      const minutes = Number(form.get("minutes") ?? 0);
      const seconds = Number(form.get("seconds") ?? 0);
      await apiClient.post("/staff/comdesk/calls", {
        direction: String(form.get("direction")) as Direction,
        number: String(form.get("number") ?? "").trim(),
        counterpartyName: String(form.get("name") ?? "").trim() || undefined,
        outcome,
        durationSeconds: minutes * 60 + seconds,
        notes: String(form.get("notes") ?? "").trim() || undefined,
      });
    },
    onSuccess: () => {
      setRefusal(null);
      setLogging(false);
      void queryClient.invalidateQueries({ queryKey: ["comdesk", "calls"] });
    },
    onError: (error: unknown) => {
      // The server refuses a call for stated reasons — a connected call with no
      // duration, for one. Showing its own words is more use than "failed".
      const said = (
        error as { response?: { data?: { error?: { message?: string } } } }
      )?.response?.data?.error?.message;
      setRefusal(said ?? "The call could not be logged.");
    },
  });

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Phone className="h-5 w-5 text-sky-600" /> ComDesk — Phone
        </h1>
        <div className="flex items-center gap-2">
          <label className="relative w-64">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search number, name or notes"
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-sky-500 focus:outline-none"
            />
          </label>
          <button
            onClick={() => setLogging((open) => !open)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700"
          >
            <Plus className="h-4 w-4" /> Log a call
          </button>
        </div>
      </div>

      {state.data && <SwitchBanner state={state.data} />}

      {mine.data && <MyExtensionCard mine={mine.data} />}

      {logging && (
        <form
          className="grid gap-3 rounded-lg border border-gray-200 bg-white p-4 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            logCall.mutate(new FormData(event.currentTarget));
          }}
        >
          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Direction
            </span>
            <select
              name="direction"
              defaultValue="outbound"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="outbound">We called them</option>
              <option value="inbound">They called us</option>
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700">Number</span>
            <input
              name="number"
              required
              placeholder="+49 30 1234567"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Name <span className="font-normal text-gray-400">(optional)</span>
            </span>
            <input
              name="name"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Outcome
            </span>
            <select
              name="outcome"
              defaultValue="connected"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              {(Object.keys(OUTCOME_LABEL) as Outcome[]).map((outcome) => (
                <option key={outcome} value={outcome}>
                  {OUTCOME_LABEL[outcome]}
                </option>
              ))}
            </select>
          </label>

          <fieldset className="text-sm">
            <legend className="mb-1 block font-medium text-gray-700">
              How long
            </legend>
            <div className="flex items-center gap-2">
              <input
                name="minutes"
                type="number"
                min={0}
                defaultValue={0}
                aria-label="Minutes"
                className="w-20 rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <span className="text-gray-500">min</span>
              <input
                name="seconds"
                type="number"
                min={0}
                max={59}
                defaultValue={0}
                aria-label="Seconds"
                className="w-20 rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <span className="text-gray-500">sec</span>
            </div>
          </fieldset>

          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block font-medium text-gray-700">Notes</span>
            <textarea
              name="notes"
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>

          {refusal && (
            <p className="text-sm text-red-700 sm:col-span-2">{refusal}</p>
          )}

          <div className="flex gap-2 sm:col-span-2">
            <button
              type="submit"
              disabled={logCall.isPending}
              className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
            >
              {logCall.isPending ? "Saving…" : "Save call"}
            </button>
            <button
              type="button"
              onClick={() => {
                setLogging(false);
                setRefusal(null);
              }}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {calls.isLoading ? (
          <div className="flex items-center gap-2 p-8 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading calls…
          </div>
        ) : calls.isError ? (
          <div className="p-6 text-sm text-red-700">
            The call log could not be read.
          </div>
        ) : !calls.data?.length ? (
          <div className="p-8 text-sm text-gray-500">
            No calls recorded yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">Who</th>
                <th className="px-4 py-2 font-medium">Outcome</th>
                <th className="px-4 py-2 font-medium tabular-nums">Length</th>
                <th className="px-4 py-2 font-medium">Notes</th>
                <th className="px-4 py-2 font-medium">Logged by</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {calls.data.map((call) => (
                <tr key={call.id} className="align-top">
                  <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                    {when(call.startedAt)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5">
                      {call.direction === "inbound" ? (
                        <PhoneIncoming className="h-3.5 w-3.5 text-emerald-600" />
                      ) : (
                        <PhoneOutgoing className="h-3.5 w-3.5 text-sky-600" />
                      )}
                      {/* A tel: link, and the number exactly as it was stored
                          (AC-004). Normalising it here would be the same
                          mistake one layer up. */}
                      <a
                        href={`tel:${call.number}`}
                        className="font-medium text-gray-900 hover:text-sky-700"
                      >
                        {call.number}
                      </a>
                    </span>
                    {call.counterpartyName && (
                      <span className="mt-0.5 block text-xs text-gray-500">
                        {call.counterpartyName}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${OUTCOME_STYLE[call.outcome]}`}
                    >
                      {OUTCOME_LABEL[call.outcome]}
                    </span>
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-600">
                    {duration(call.durationSeconds)}
                  </td>
                  <td className="max-w-xs px-4 py-3 text-gray-600">
                    {call.notes ?? "—"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                    {call.source === "freeswitch"
                      ? "the switch"
                      : call.handledBy}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
